import { Button, FormikField, P, theme } from '@ahmdigital/ui';
import styled from 'styled-components';

const { colors, media, mediaQueries } = theme;

const DESKTOP_BORDER_RADIUS = '3rem';
const BOX_SHADOW = '0px 4px 7px 0px rgba(128, 128, 128, 0.2)';
const BORDER_WIDTH = '1px';
const PANEL_SPACING_TO_EDGE = '1.5rem';

export const FieldWrapper = styled.div<{
  isFirst?: boolean;
  isLast?: boolean;
  isOpen?: boolean;
  hasButton: boolean;
  hasError: boolean;
  isButtonHovered: boolean;
}>`
  border-color: ${({ isOpen, hasError }) => {
    if (hasError) return colors.red;
    if (isOpen) return colors.purple;
    return colors.grey10;
  }};
  border-radius: 1rem;
  border-style: solid;
  border-width: ${BORDER_WIDTH};
  border-bottom-left-radius: ${({ isOpen }) => (isOpen ? '0' : '')};
  border-bottom-right-radius: ${({ isOpen }) => (isOpen ? '0' : '')};
  box-shadow: ${BOX_SHADOW};
  margin-bottom: ${({ isOpen }) => (isOpen ? '0' : '1rem')};
  padding: 1rem;
  background-color: ${({ hasError }) => (hasError ? colors.redTint : 'transparent')};

  ${mediaQueries.above(media.md)} {
    width: ${({ hasButton }) => (hasButton ? '21rem' : 'fill-available')};
    border: unset;
    border-radius: unset;
    border-radius: ${({ hasError }) => (hasError ? '3rem' : 'unset')};
    box-shadow: unset;
    border-bottom-left-radius: ${({ isFirst }) => (isFirst ? `${DESKTOP_BORDER_RADIUS}` : '')};
    border-top-left-radius: ${({ isFirst }) => (isFirst ? `${DESKTOP_BORDER_RADIUS}` : '')};
    border-bottom-right-radius: ${({ isLast }) => (isLast ? `${DESKTOP_BORDER_RADIUS}` : '')};
    border-top-right-radius: ${({ isLast }) => (isLast ? `${DESKTOP_BORDER_RADIUS}` : '')};
    display: flex;
    margin-bottom: 0;
    padding: 0;

    &:focus {
      border-radius: 3rem;
      outline: 2px dotted ${colors.grey50};
      outline-offset: 2px;
      z-index: 1;
      position: relative;
    }

    ${({ isButtonHovered, hasError }) =>
      isButtonHovered &&
      `
      background-color: ${hasError ? colors.redTint : colors.grey10};
      border-radius: 6.5rem;
    `}

    &:hover {
      background-color: ${({ isOpen, hasError }) => {
        if (hasError) return colors.redTint;
        if (isOpen) return colors.white;
        return colors.grey10;
      }};
      border-radius: 6.5rem;
      cursor: pointer;
    }
  }
  ${mediaQueries.above(media.lg)} {
    width: ${({ hasButton }) => (hasButton ? '31.25rem' : 'fill-available')};
  }

  ${mediaQueries.above(media.xl)} {
    width: ${({ hasButton }) => (hasButton ? '34.25rem' : 'fill-available')};
  }
`;

export const IconWrapper = styled.div`
  align-items: center;
  display: flex;
`;

export const InputSeparator = styled.div<{ isVisible: boolean; isOpen?: boolean }>`
  display: none;

  ${mediaQueries.above(media.md)} {
    align-self: center;
    display: ${({ isOpen }) => (isOpen ? 'none' : 'flex')};
    border-right: 1px solid ${({ isVisible }) => (isVisible ? colors.grey20 : colors.white)};
    height: 4rem;
  }
`;

export const InnerInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 1rem;

  ${mediaQueries.above(media.md)} {
    max-width: 14rem;
    justify-content: normal;
    gap: 0.5rem;
  }

  ${mediaQueries.above(media.lg)} {
    max-width: 15.75rem;
  }

  ${mediaQueries.above(media.xl)} {
    gap: 1rem;
  }
`;

export const InputWrapper = styled.div<{
  isFirst?: boolean;
  isLast?: boolean;
  isOpen?: boolean;
  hasButton: boolean;
  hasError: boolean;
}>`
  border-radius: 6.5rem;
  border-top-right-radius: ${({ isOpen }) => (isOpen ? '6.5rem' : '0')};
  border-bottom-right-radius: ${({ isOpen }) => (isOpen ? '6.5rem' : '0')};

  ${mediaQueries.above(media.md)} {
    border: ${({ isOpen, hasError }) => {
      if (hasError) return `${BORDER_WIDTH} solid ${colors.red}`;
      if (isOpen) return `${BORDER_WIDTH} solid ${colors.purple}`;
      return 'unset';
    }};
    box-shadow: ${({ isOpen }) => (isOpen ? `${BOX_SHADOW}` : 'unset')};
    width: ${({ hasButton }) => (hasButton ? `100%` : 'auto')};
    padding: ${({ isOpen, hasError }) =>
      isOpen || hasError ? `calc(1rem - ${BORDER_WIDTH}) calc(2rem - ${BORDER_WIDTH})` : '1rem 2rem'};

    & input:focus {
      outline: none;
      outline-offset: none;
    }
  }
`;

export const Label = styled.span`
  font-size: 0.875rem;
  line-height: 1.5rem;
`;

export const ModalWrapper = styled.div<{
  isOpen: boolean | undefined;
  hasError: boolean;
  isFirst?: boolean;
  isLast?: boolean;
}>`
  background-color: ${colors.white};
  border-style: solid;
  border-width: 1px;
  border-color: ${({ isOpen, hasError }) => {
    if (hasError) return colors.red;
    if (isOpen) return colors.purple;
    return colors.grey10;
  }};
  border-top: none;
  border-bottom-left-radius: ${({ isOpen }) => (isOpen ? '1rem' : '0')};
  border-bottom-right-radius: ${({ isOpen }) => (isOpen ? '1rem' : '0')};
  box-shadow: ${BOX_SHADOW};
  margin-bottom: ${({ isOpen }) => (isOpen ? '1rem' : '0')};
  padding: 1rem;

  ${mediaQueries.above(media.md)} {
    border-radius: 1rem;
    border-color: ${colors.purple};
    border-top: 1px solid ${colors.purple};
    box-shadow: ${BOX_SHADOW};
    margin-top: 1rem;
    margin-bottom: 0;
    padding: 2rem;
    position: absolute;
    width: max-content;
    top: 8.25rem;
    z-index: 100;
    left: ${({ isFirst }) => (isFirst ? `-${PANEL_SPACING_TO_EDGE}` : 'unset')};
    right: ${({ isLast }) => (isLast ? `-${PANEL_SPACING_TO_EDGE}` : 'unset')};
  }

  ${mediaQueries.above(media.lg)} {
    right: unset;
  }
`;

export const PlaceholderText = styled(P)<{ hasValue: boolean }>`
  color: ${({ hasValue }) => (hasValue ? colors.black : colors.grey40)};
  font-size: 1.25rem;
`;

export const StyledButton = styled(Button)`
  border-radius: 1rem;
  width: 100%;

  ${mediaQueries.above(media.md)} {
    position: absolute;
    right: 1.25rem;
    align-items: center;
    border-radius: 4.5rem;
    margin-left: 3rem;
    padding: 0;
    z-index: 2;
    width: 3.25rem;
    height: 3.25rem;
    display: flex;
    justify-content: center;
    top: 0.75rem;
    top: 18px;
  }

  ${mediaQueries.below(media.lg)} {
    & svg {
      margin: 0;
    }
  }

  ${mediaQueries.above(media.lg)} {
    top: 15px;
    padding: 0.75rem 2rem;
    height: auto;
    max-width: 14rem;
    width: auto;
    right: 1.5rem;
  }
`;

export const StyledFormikField = styled(FormikField)<{ isLast: boolean }>`
  margin-bottom: 0;
  margin-right: 1rem;
  width: 100%;

  ${mediaQueries.above(media.md)} {
    margin-right: 0;
    width: auto;
  }

  & input {
    font-size: 1.25rem;
  }
`;

export const ToggleWrapper = styled.div<{ isLast?: boolean }>`
  display: flex;
  flex-direction: column;

  ${mediaQueries.above(media.md)} {
    width: ${({ isLast }) => (isLast ? '10rem' : '13rem')};
  }

  ${mediaQueries.above(media.xl)} {
    width: ${({ isLast }) => (isLast ? '10rem' : '14rem')};
  }
`;

export const Wrapper = styled.div<{ isLast?: boolean }>`
  position: relative;

  ${mediaQueries.above(media.md)} {
    display: flex;
  }
`;

export const ErrorText = styled.div`
  display: block;
  color: ${colors.redDark};
  margin-bottom: 1.5rem;

  ${mediaQueries.above(media.md)} {
    display: block;
    font-size: 14px;
    margin-bottom: 0;
    margin-top: 0.5rem;
    position: absolute;
    bottom: -1.75rem;
  }
`;
